import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { Context, Hooks, Request, Util } from "@xt/xtbase";
const { XtContext } = Context;
const { useTranslation } = Hooks;
const { Big } = Util;
import cx from "classnames";
import store from "store";
import Socket from "utils/socket/public";
import { get_cfd_ticker24h, get_ticker24h } from "api/v4/market";
import useIntervalAsync from "hooks/useIntervalAsync";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import useFormatBigNumber from "hooks/useFormatBigNumber";
import { getOneByThreeStatus } from "utils/method";
import { ClsUpDownEnum } from "store/app";
import { Drawer, Dropdown, MenuProps } from "antd";
import XtSvg from "@/components/Xt/Svg";
import CPMT_market from "@/components/Pages/Trade/_cmpt/market";
import Star from "@/components/Pages/Trade/_cmpt/market/container/star";
import TradingSettingsModal from "./tradingSettingsModal";
import TradingRulesDrawer from "./tradingRulesDrawer";
import AiSelectNavButton from "@/components/App/AiSelectNavButton";
import styles from "./index.module.scss";

export interface TickerProps {
  s: string; //市场名称, btc_usdt
  c: string; //当前价格, 100.000
  cr: string; //涨跌幅 0.0582 -> 5.8%
  cv: string; //价格变动值
  h: string; //24H最高价 25891.63
  l: string; //24H最低价 24188.76
  q: string; //24H成交数量 btc
  v: string; //24H成交金额 usdt
}

interface HeaderProps {
  viewMode?: "standard" | "pro";
  onViewModeChange?: (mode: "standard" | "pro") => void;
  hideViewModeSwitch?: boolean; // 是否隐藏标准/专业切换按钮
  hideMoreBtn?: boolean; // 是否隐藏更多按钮
  hideMarketSwitch?: boolean; // 是否隐藏交易对切换（Prop Trading 交易页使用）
  /** 点击「交易教程」时打开新手引导 */
  onOpenTutorial?: () => void;
  // variant 区分展示：detail 展示 24h 统计；trade 展示切换/更多按钮
  variant?: "detail" | "trade";
}

const Header: React.FC<HeaderProps> = ({
  viewMode,
  onViewModeChange,
  hideViewModeSwitch = false,
  hideMoreBtn = false,
  hideMarketSwitch = false,
  onOpenTutorial,
  variant = "detail",
}) => {
  const router = useRouter();
  const [appState] = React.useContext(XtContext);
  const t = useTranslation();
  const { name, formatName } = store.market;
  const { coinPricePrecisionMarket, coinQuantityPrecisionMarket } = useCoinMemo();
  const { isH5 } = store.app;

  // 交易对选择弹框状态
  const [marketDrawerOpen, setMarketDrawerOpen] = useState(false);
  const [marketPopoverVisible, setMarketPopoverVisible] = useState(false);
  const marketPopoverRef = useRef<HTMLDivElement>(null);

  // 交易设置弹窗状态
  const [tradingSettingsOpen, setTradingSettingsOpen] = useState(false);
  // 交易规则底部弹窗状态
  const [tradingRulesOpen, setTradingRulesOpen] = useState(false);
  // 专业版「更多」下拉菜单显隐（受控，便于点击页面其他区域关闭）
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);

  const [ticker, setTicker] = useState<TickerProps>();

  // 获取当前交易对的 symbolId (CFD 接口使用，暂时注释)
  // const symbolId = useMemo(() => {
  //   if (!store.market.symbols || !name) return undefined;
  //   const currentSymbol = store.market.symbols.find((s: any) => s.symbol === name);
  //   return currentSymbol?.id;
  // }, [name, store.market.symbols]);

  const apiReq = useCallback(async () => {
    // 确保使用带 _cfd 后缀的 symbol
    // console.log("Request.XtAxios.asyncAwait ----------> get_ticker24h");
    const symbol = name && !name.endsWith("_cfd") ? `${name}_cfd` : name;
    const { err, data } = await Request.XtAxios.asyncAwait(get_ticker24h, {
      params: { symbol },
    });
    if (!err && data && data.length) {
      setTicker(data[0]);
      store.cfdView.updateState({ cfdTicker: data[0] });
    }

    // CFD 接口调用（暂时注释，后续可能再切换）
    // if (!symbolId) return;
    // const { err, data } = await Request.XtAxios.asyncAwait(get_cfd_ticker24h, {
    //   params: { symbolId },
    // });
    // if (!err && data) {
    //   // 转换新接口数据结构为 TickerProps 格式
    //   // data 已经是 result 层级
    //   const tickerData: TickerProps = {
    //     s: data.symbol || name,
    //     c: String(data.lastPrice || 0),
    //     cr: String((data.priceChangePercent || 0) / 100), // 百分比转为小数
    //     cv: String(data.priceChange || 0),
    //     h: String(data.highPrice || 0),
    //     l: String(data.lowPrice || 0),
    //     q: String(data.volume || 0),
    //     v: String(data.quoteVolume || 0),
    //   };
    //   setTicker(tickerData);
    // }
  }, [name]);
  const { clear } = useIntervalAsync(apiReq, 5e3);

  const getMessage = (market = name) => {
    return {
      event: `ticker@${market}`,
    };
  };

  // 节流写 state/store，降低重渲染（200ms 进一步减轻卡顿）
  const TICKER_THROTTLE_MS = 200;
  const tickerRef = useRef<TickerProps | null>(null);
  const tickerFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFlushAtRef = useRef(0);
  const prevNameRef = useRef(name);

  const flushTicker = useCallback(() => {
    lastFlushAtRef.current = Date.now();
    tickerFlushTimerRef.current = null;
    const next = tickerRef.current;
    setTicker(next ?? undefined);
    store.cfdView.updateState({
      cfdTicker: next ?? undefined,
      cfdTickerUpdateTime: next != null ? Date.now() : undefined,
    });
  }, []);

  const wsCallback = useCallback(
    (data: TickerProps) => {
      if (data.s !== store.market.name) {
        console.log("ticker@ name !== data.s", store.market.name, data.s);
        return;
      }
      clear();
      tickerRef.current = data;
      const now = Date.now();
      if (now - lastFlushAtRef.current >= TICKER_THROTTLE_MS) {
        flushTicker();
      } else if (!tickerFlushTimerRef.current) {
        tickerFlushTimerRef.current = setTimeout(() => {
          flushTicker();
        }, TICKER_THROTTLE_MS - (now - lastFlushAtRef.current));
      }
    },
    [clear, flushTicker]
  );

  useEffect(() => {
    // 仅当切换交易对时清空价格，避免因 wsCallback 依赖变化重跑 effect 时误清空
    if (prevNameRef.current !== name) {
      prevNameRef.current = name;
      setTicker(undefined);
      store.cfdView.updateState({ cfdTicker: undefined, cfdTickerUpdateTime: undefined, lastKlineBarTime: undefined });
    }

    Socket.addChannel(getMessage(), wsCallback);

    return () => {
      if (tickerFlushTimerRef.current) {
        clearTimeout(tickerFlushTimerRef.current);
        tickerFlushTimerRef.current = null;
      }
      tickerRef.current = null;
      // 不在 cleanup 里清空 setTicker/cfdView，否则 effect 因依赖重跑时会闪一下然后显示 --
      Socket.removeChannel(getMessage());
    };
  }, [name, wsCallback]);

  // 格式化价格和涨跌幅
  const contractName = useMemo(() => {
    return store.market.formatCfdName(name)+ " " + t("cfd.contract");
  }, [name, t]);

  const price = useMemo(() => {
    if (!ticker) return "--";
    // 如果 precision 为 0 或未定义，使用默认的 2 位小数，否则使用配置的精度
    const precision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    return Big(ticker.c || 0).toFixedCy(precision);
  }, [ticker, coinPricePrecisionMarket]);

  const changePercent = useMemo(() => {
    if (!ticker) return "--";
    const sign = getOneByThreeStatus({ gt: "+", lt: "", eq: "" }, ticker.cv);
    const rise = Big(ticker.cr || 0)
      .times(100)
      .toFixed(2);
    return `${sign}${rise}%`;
  }, [ticker]);

  const isPositive = useMemo(() => {
    if (!ticker || !ticker.cv) return false;
    return Big(ticker.cv).gt(0);
  }, [ticker]);

  const changeCls = useMemo(() => {
    if (!ticker) return "";
    return getOneByThreeStatus({ gt: styles.positive, lt: styles.negative }, ticker.cv);
  }, [ticker]);

  const volumeBase = useFormatBigNumber(ticker ? ticker.q : 0, coinQuantityPrecisionMarket);
  const volumeQuote = useFormatBigNumber(ticker ? ticker.v : 0, coinPricePrecisionMarket);

  const highPrice = useMemo(() => {
    if (!ticker) return "--";
    return Big(ticker.h || 0).toFixedCy(coinPricePrecisionMarket);
  }, [ticker, coinPricePrecisionMarket]);

  const lowPrice = useMemo(() => {
    if (!ticker) return "--";
    return Big(ticker.l || 0).toFixedCy(coinPricePrecisionMarket);
  }, [ticker, coinPricePrecisionMarket]);

  // 处理交易对选择弹框
  const handleMarketSelectClick = useCallback((e?: React.MouseEvent) => {
    // 阻止事件冒泡，避免触发其他点击事件
    if (e) {
      e.stopPropagation();
    }
    if (isH5) {
      setMarketDrawerOpen(true);
    } else {
      setMarketPopoverVisible(!marketPopoverVisible);
    }
  }, [isH5, marketPopoverVisible]);

  // 处理左侧区域点击（hideMarketSwitch 时禁用）
  const handleLeftClick = useCallback((e: React.MouseEvent) => {
    if (hideMarketSwitch) return;
    // 如果点击的是右侧按钮区域，不处理
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.right}`)) {
      return;
    }
    handleMarketSelectClick(e);
  }, [handleMarketSelectClick, hideMarketSwitch]);

  const handleCloseMarketDrawer = useCallback(() => {
    setMarketDrawerOpen(false);
  }, []);

  // 选中任意交易对时关闭弹框（包括选中当前 pair 时也关闭）
  const handleCloseMarketSelect = useCallback(() => {
    setMarketDrawerOpen(false);
    setMarketPopoverVisible(false);
  }, []);

  // 关闭 popover 当点击外部时
  useEffect(() => {
    if (!marketPopoverVisible || isH5) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        marketPopoverRef.current &&
        !marketPopoverRef.current.contains(target) &&
        !(target as HTMLElement).closest(`.${styles.left}`)
      ) {
        setMarketPopoverVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [marketPopoverVisible, isH5]);

  // 当交易对改变时关闭弹框
  useEffect(() => {
    setMarketDrawerOpen(false);
    setMarketPopoverVisible(false);
  }, [name]);

  // 处理行情图标点击 - 跳转到当前币种的合约交易页面
  const handleMarketIconClick = useCallback(() => {
    // 使用当前交易对的 symbol（需要添加 _cfd 后缀）
    const symbol = name || "btc_usdt";
    const cfdSymbol = symbol.endsWith("_cfd") ? symbol : `${symbol}_cfd`;
    router.push(`/futures/detail/${cfdSymbol}`);
  }, [router, name]);

  // 处理更多菜单点击
  const handleMenuClick = useCallback(({ key }: { key: string }) => {
    switch (key) {
      case "rules":
        setTradingRulesOpen(true);
        break;
      case "tutorial":
        onOpenTutorial?.();
        break;
      case "settings":
        setTradingSettingsOpen(true);
        break;
      default:
        break;
    }
  }, [onOpenTutorial]);

  // 下拉菜单配置
  const menuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "rules",
        label: t("cfd.tradingRules"),
      },
      {
        key: "tutorial",
        label: t("cfd.tradingTutorial"),
      },
      {
        key: "settings",
        label: t("cfd.tradingSettings"),
      },
    ],
    [t]
  );

  const stats = useMemo<
    Array<{
      label: string;
      value: string;
      cls?: string;
    }>
  >(
    () => [
      // { label: t("trade.24hChange"), value: changePercent, cls: changeCls },
      { label: t("trade.24HHigh"), value: highPrice },
      { label: t("trade.24HLow"), value: lowPrice },
      { label: t("trade.homeMarketTradeUp"), value: volumeBase },
      { label: t("trade.homeMarketTradeUp2"), value: volumeQuote },
    ],
    [changePercent, changeCls, highPrice, lowPrice, volumeBase, volumeQuote, name, t]
  );

  return (
    <div className={styles.header}>
      <div className={styles.left} onClick={handleLeftClick}>
        <div className={styles.topRow}>
            <div className={styles.nameRow} data-guide-target="cfdSymbolNameRow">
            <span className={styles.name}>{contractName}</span>
            {!hideMarketSwitch && (
              <div className={styles.marketSelectBtn}>
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="4" viewBox="0 0 8 4" fill="none">
                  <path d="M8 0H0L4 4L8 0Z" fill="#999999" />
                </svg>
              </div>
            )}
            {!isH5 && marketPopoverVisible && (
              <div ref={marketPopoverRef} className={styles.marketPopover}>
                <CPMT_market clsSearch={styles.marketSearch} onMarketSelect={handleCloseMarketSelect} />
              </div>
            )}
          </div>
          <div className={styles.priceRow}>
            <span className={styles.price}>{price}</span>
            <span className={cx(styles.change, { [styles.positive]: isPositive, [styles.negative]: !isPositive })}>
              {changePercent}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {variant === "detail" && (
          <div className={styles.stats}>
            {stats.map((item) => (
              <div key={item.label} className={styles.statRow}>
                <p>{item.label}</p>
                <span className={cx(styles.statValue, item.cls)}>{item.value}</span>
              </div>
            ))}
          </div>
        )}

        {variant === "trade" && (
          <div className={styles.actions}>
            {/* 第一行：行情图标和更多按钮 */}
            <div className={styles.actionsTop}>
              {/* 收藏 */}
              <div className={styles.starWrap} onClick={(e) => e.stopPropagation()}>
                <Star symbol={name} iconColor="#fff" />
              </div>

              <div className={styles.aiSelectAction} onClick={(e) => e.stopPropagation()}>
                <AiSelectNavButton symbol={name} aria-label={t("header.aicopy")} />
              </div>

              {/* 行情图标 */}
              <div className={styles.marketIcon} onClick={handleMarketIconClick}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <g clipPath="url(#clip0_7120_16311)">
                  <path d="M4.12695 5H5.05273C5.98285 5.00006 6.7373 5.74655 6.7373 6.66699V11.333C6.7373 12.2534 5.98285 12.9999 5.05273 13H4.12695V16H2.52637V13H1.68457C0.754407 13 0 12.2535 0 11.333V6.66699C0 5.74652 0.754407 5 1.68457 5H2.52637V1H4.12695V5ZM12.5098 3H13.4736C14.4038 3 15.1582 3.74652 15.1582 4.66699V11.333C15.1582 12.2535 14.4038 13 13.4736 13H12.5098V16H10.9102V13H10.1055C9.17531 13 8.4209 12.2535 8.4209 11.333V4.66699C8.4209 3.74652 9.17531 3 10.1055 3H10.9102V0H12.5098V3ZM1.68457 11.333H5.05273V6.66699H1.68457V11.333ZM10.1055 11.333H13.4736V4.66699H10.1055V11.333Z" fill="white"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_7120_16311">
                  <rect width="16" height="16" fill="white"/>
                  </clipPath>
                  </defs>
                </svg>
              </div>
              {/* 更多按钮下拉菜单：受控 + 挂到 body，点页面其他处可关闭 */}
              {!hideMoreBtn && (
                <Dropdown
                  open={moreDropdownOpen}
                  onOpenChange={setMoreDropdownOpen}
                  menu={{ items: menuItems, onClick: (info) => { handleMenuClick(info); setMoreDropdownOpen(false); } }}
                  trigger={["click"]}
                  placement="bottomRight"
                  overlayClassName={styles.moreDropdown}
                  getPopupContainer={() => document.body}
                >
                  <button className={styles.moreBtn} onClick={(e) => e.preventDefault()}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="3" viewBox="0 0 16 3" fill="none">
                    <path d="M2.7998 1.7998C2.7998 2.35209 2.35209 2.7998 1.7998 2.7998L1 2.7998C0.447715 2.7998 -1.02813e-07 2.35209 -7.8672e-08 1.7998L-4.37114e-08 0.999999C-1.95703e-08 0.447715 0.447715 -6.6231e-07 1 -6.38169e-07L1.7998 -6.03209e-07C2.35209 -5.79068e-07 2.7998 0.447715 2.7998 0.999999L2.7998 1.7998ZM9.19922 1.7998C9.19922 2.35209 8.7515 2.7998 8.19922 2.7998L7.39941 2.7998C6.84713 2.7998 6.39941 2.35209 6.39941 1.7998L6.39941 1C6.39941 0.447715 6.84713 -3.82583e-07 7.39941 -3.58442e-07L8.19922 -3.23481e-07C8.7515 -2.9934e-07 9.19922 0.447715 9.19922 1L9.19922 1.7998ZM15.5996 1.7998C15.5996 2.35209 15.1519 2.7998 14.5996 2.7998L13.7998 2.7998C13.2475 2.7998 12.7998 2.35209 12.7998 1.7998L12.7998 1C12.7998 0.447715 13.2475 -1.02813e-07 13.7998 -7.86715e-08L14.5996 -4.37109e-08C15.1519 -1.95698e-08 15.5996 0.447715 15.5996 1L15.5996 1.7998Z" fill="white"/>
                  </svg>
                  </button>
                </Dropdown>
              )}
            </div>

            {/* 第二行：标准/专业切换 */}
            {!hideViewModeSwitch && viewMode !== undefined && onViewModeChange && (
              <div className={styles.viewModeSwitch}>
                <button
                  className={cx(styles.modeBtn, { [styles.active]: viewMode === "standard" })}
                  onClick={() => onViewModeChange("standard")}
                >
                  {t("cfd.standard")}
                </button>
                <button
                  className={cx(styles.modeBtn, { [styles.active]: viewMode === "pro" })}
                  onClick={() => onViewModeChange("pro")}
                >
                  {t("cfd.professional")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* H5 端交易对选择 Drawer */}
      {isH5 && (
        <Drawer
          className={styles.marketDrawer}
          closable={false}
          title={t("trade.market2")}
          placement="bottom"
          height="80vh"
          open={marketDrawerOpen}
          onClose={handleCloseMarketDrawer}
          extra={
            <button className={cx("btnTxt", "btnHover")} onClick={handleCloseMarketDrawer}>
              <XtSvg icon="close" />
            </button>
          }
        >
          <CPMT_market clsSearch={styles.marketSearch} onMarketSelect={handleCloseMarketSelect} />
        </Drawer>
      )}

      {/* 交易设置弹窗 */}
      <TradingSettingsModal open={tradingSettingsOpen} onClose={() => setTradingSettingsOpen(false)} />

      {/* 交易规则底部弹窗 */}
      <TradingRulesDrawer open={tradingRulesOpen} onClose={() => setTradingRulesOpen(false)} />
    </div>
  );
};

export default observer(Header);

